import { inject, Injectable } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutItemSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-item-settings';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DashboardsService } from '@common-modules/right-panel/dashboards/dashboards.service';
import { PermissionsRequest } from '@common-modules/shared/auth/models/permissions-request';
import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { map, Observable, of, Subject, switchMap, take } from 'rxjs';
import { CreateDashboardPopupComponent } from './create-dashboard-popup/create-dashboard-popup.component';
import { DashboardIdentity } from './dashboard-identity';
import { DashboardChildWidgetParams, DashboardWidgetParams } from './dashboard-widget-params';

/**
 * This service encapsulates flows that are between dashboards and the standard right panel.
 */

@Injectable()
export class DashboardManagerService {
  private readonly _addDashboard$ = new Subject<DynamicLayoutItemSettings>();
  readonly addDashboard$ = this._addDashboard$.asObservable();

  private readonly _updateDashboardChild$ = new Subject<DashboardChildWidgetParams>();
  readonly updateDashboardChild$ = this._updateDashboardChild$.asObservable();

  private readonly _updateCardAndSettingsInSettingsComponent$ =
    new Subject<DashboardCartAndSettings>();
  readonly updateCartAndSettingsInSettingsComponet$ =
    this._updateCardAndSettingsInSettingsComponent$.asObservable();

  private readonly _dialogService = inject(DialogService);
  private readonly _dynamicLayoutBuilderService = inject(DynamicLayoutBuilderService);
  private readonly _dashboardsService = inject(DashboardsService);

  private readonly _dashboardLayoutSimpleSettings: SimpleDynamicLayoutSettings = {
    layoutKey: null,
    layoutArea: 'RP_Dashboard',
  };

  /**
   * Loads dashboard and get its layout settings.
   */
  loadDashboardsGroup(): Observable<DynamicLayoutSettings> {
    return this._dashboardsService.loadGroup().pipe(
      map((dashboardGroup) => this._dashboardsService.mapDashboardGroupToLayout(dashboardGroup)),
      map((dashboardGroup) => {
        dashboardGroup.disableReorder = true;
        return dashboardGroup;
      })
    );
  }

  /**
   * Convert layout settings back to dashboard and save it.
   */
  createDashboardsGroup(dynamicLayoutSettings: DynamicLayoutSettings): Observable<any> {
    return this.getDashboardsGroupPermissions().pipe(
      take(1),
      switchMap((permissions) => {
        if (permissions?.canCreate) {
          const groupDefinition =
            this._dashboardsService.mapLayoutToDashboardGroup(dynamicLayoutSettings);
          return this._dashboardsService.createGroup(groupDefinition);
        }

        return of(false);
      })
    );
  }

  updateDashboardsGroup(dynamicLayoutSettings: DynamicLayoutSettings): Observable<any> {
    return this.getDashboardsGroupPermissions().pipe(
      take(1),
      switchMap((permissions) => {
        if (permissions?.canUpdate) {
          const groupDefinition =
            this._dashboardsService.mapLayoutToDashboardGroup(dynamicLayoutSettings);
          return this._dashboardsService.updateGroup(groupDefinition);
        }

        return of(false);
      })
    );
  }

  showNewDashboardPopup(): void {
    const config = new MatDialogConfig();
    config.height = '200px';
    this._dialogService
      .openComponent(CreateDashboardPopupComponent, config)
      .afterClosed()
      .subscribe((result) => {
        if (result?.dashboard) {
          this.createDashboard(result.dashboard);
        }
      });
  }

  getDashboardsGroupPermissions(): Observable<PermissionsRequest> {
    return this._dashboardsService.getDashboardsGroupPermissions();
  }

  updateDashboardChild(dashboardChild: DashboardChildWidgetParams): void {
    this._updateDashboardChild$.next(dashboardChild);
  }

  updatedcardAndSettingsInSettingsComponent(cardAndSettings: DashboardCartAndSettings) {
    this._updateCardAndSettingsInSettingsComponent$.next(cardAndSettings);
  }

  private createDashboard(dashboard: DashboardIdentity): void {
    const layoutKey = dashboard.id;
    const simpleSettings = { ...this._dashboardLayoutSimpleSettings, layoutKey };
    const simpleItemSettings = this.buildWidgetFromDashboard(dashboard);

    this._dynamicLayoutBuilderService
      .buildExternalItem(simpleSettings, simpleItemSettings)
      .subscribe((itemSettings: DynamicLayoutItemSettings) => {
        this._addDashboard$.next(itemSettings);
      });
  }

  private buildWidgetFromDashboard(dashboard: DashboardIdentity): SimpleDynamicLayoutSettingsItem {
    return {
      widgetName: 'DashboardWidgetComponent',
      id: dashboard.id,
      title: dashboard.title,
      params: { dashboard } as DashboardWidgetParams,
    } as SimpleDynamicLayoutSettingsItem;
  }
}
